import { Alert } from '@mui/material';
import React, { createContext, useReducer, useContext, ReactNode, useRef } from 'react';

// Define the structure of the user's information
interface UserInfo {
  id: string;
  name: string;
  username: string;
  email: string;
  mobile: string;
  profileImage: string;
  is2FA: boolean;
  role: string;
  gender: string;
  timezone: {
    id: number;
    country_code: string;
    zone_name: string;
  };
}

// Define the structure of the authentication state
interface AuthState {
  token: string | null;
  userInfo: UserInfo | null;
}

// Define the authentication context type with setAuthData and logout methods
interface AuthContextType extends AuthState {
  setAuthData: (token: string | null, userInfo: UserInfo | null) => void;
  logout: () => void;
}

// Define actions for the authentication reducer
type AuthAction =
  | { type: 'SET_AUTH_DATA'; token: string | null; userInfo: UserInfo | null }
  | { type: 'LOGOUT' };

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define the initial state for the reducer
const initialState: AuthState = {
  token: localStorage.getItem('token') || null,
  userInfo: localStorage.getItem('userInfo') ? JSON.parse(localStorage.getItem('userInfo')!) : null,
};

// Reducer function to handle authentication state changes
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_AUTH_DATA':
      return {
        ...state,
        token: action.token,
        userInfo: action.userInfo,
      };
    case 'LOGOUT':
      return { ...state, token: null, userInfo: null };
    default:
      return state;
  }
};

// Authentication provider component to wrap application components
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref to store timeout ID
  const logoutWarningTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Set authentication data and store token in localStorage
  const setAuthData = (token: string | null, userInfo: UserInfo | null) => {
    // If a user is logging in (token is being set for the first time), start a 90-minute logout timer
    if (token && !logoutTimeoutRef.current) {
      const loginTime = new Date(); // Capture login time
      const logoutTime = new Date(loginTime.getTime() + 5400000); // Calculate logout time (90 minutes later)

      console.log('Login time:', loginTime.toLocaleString());
      console.log('Scheduled logout time:', logoutTime.toLocaleString());

      logoutWarningTimeoutRef.current = setTimeout(() => {
        // Show an alert when session is about to expire
        alert('Your session will expire in 1 minute.');
      }, 5300000); // 89 minutes

      // Set the logout timeout for 90 minutes (5400000 milliseconds) after login
      logoutTimeoutRef.current = setTimeout(() => {
        console.log('Logging out 90 minutes after login...');
        logout(); // Call logout function
      }, 5400000); // 90 minutes

      if (token) {
        localStorage.setItem('token', token);
        console.log('Token stored in local storage:', token);  // Log the token to console
      }

      if (userInfo) {
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        console.log('User info stored in local storage:', userInfo);
      }
    }

    dispatch({ type: 'SET_AUTH_DATA', token, userInfo });
    console.log('Auth data set:', { token, userInfo });
  };

  // Clear authentication data and redirect to login on logout
  const logout = () => {
    // Clear the timeout on logout
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
    }

    dispatch({ type: 'LOGOUT' });
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    console.log('Token and user info removed from local storage.');
    window.location.href = '/'; // Redirect to login
  };

  return (
    <AuthContext.Provider value={{ ...state, setAuthData, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to access authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
